/**
 * The gym's schedule week is Sunday–Saturday in America/Chicago (it matches the
 * iflipDataLoader, which publishes the current week firmly and projects the next
 * few weeks). Shifts dated after the current week are "tentative": they may still
 * change. Display-only — nothing here affects what the server returns.
 */

const pad = (n: number) => String(n).padStart(2, '0');

/** Today's date in America/Chicago as `yyyy-MM-dd` (device-local date if Intl lacks time zones). */
export const chicagoToday = (now: Date = new Date()): string => {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: 'America/Chicago',
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
    }).formatToParts(now);
    const get = (type: string) => parts.find((p) => p.type === type)?.value ?? '';
    const y = get('year');
    const m = get('month');
    const d = get('day');
    if (y && m && d) {
      return `${y}-${m}-${d}`;
    }
  } catch {
    // fall through to the device-local date
  }
  return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}`;
};

/** The Saturday that ends the current Sun–Sat week, as `yyyy-MM-dd`. */
export const currentWeekEnd = (now: Date = new Date()): string => {
  const [y, m, d] = chicagoToday(now).split('-').map(Number);
  const day = new Date(Date.UTC(y, m - 1, d, 12));
  day.setUTCDate(day.getUTCDate() + (6 - day.getUTCDay()));
  return `${day.getUTCFullYear()}-${pad(day.getUTCMonth() + 1)}-${pad(day.getUTCDate())}`;
};

/** True when a `yyyy-MM-dd` shift date falls after the current Sun–Sat week. */
export const isTentativeDate = (isoDate: string, now: Date = new Date()): boolean =>
  !!isoDate && isoDate.slice(0, 10) > currentWeekEnd(now);
